import React, { useState, useEffect } from "react";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from "@mui/lab";
import {
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Box,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  IconButton,
  Pagination,
} from "@mui/material";
import useConfirmDialog from "services/hooks/useConfirmDialog";
import {
  Add as AddIcon,
  AccessTime,
  Phone,
  Email,
  Event,
  Description,
  Notifications,
  TrendingUp,
  Person,
  Edit,
  Delete,
  PhoneMissed,
} from "@mui/icons-material";
import { format } from "date-fns";
import { customGetAPI } from "services/utils/CustomAPI";
import { useSelector } from "react-redux";
import axios from "axios";

const activityIcons = {
  Call: <Phone />,
  Email: <Email />,
  Meeting: <Event />,
  Note: <Description />,
  Reminder: <Notifications />,
  Status_change: <TrendingUp />,
  Created: <Person />,
  Updated: <Edit />,
  Missed_call: <PhoneMissed />,
};

// const activityColors = {
//   Call: "success",
//   Email: "primary",
//   Meeting: "secondary",
//   Note: "info",
//   Reminder: "warning",
//   Status_change: "info",
//   Created: "success",
//   Updated: "warning",
// };

// const activityTypes = [
//   { value: "call", label: "Phone Call" },
//   { value: "email", label: "Email" },
//   { value: "meeting", label: "Meeting" },
//   { value: "note", label: "Note" },
//   { value: "reminder", label: "Reminder" },
// ];

export default function ContactTimeline({
  contactId,
  triggerAddActivity,
  onAddActivityOpened,
}) {
  const [activities, setActivities] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editingActivity, setEditingActivity] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const { ConfirmDialog, showConfirmDialog } = useConfirmDialog();
  const [activityTypes, setActivityTypes] = useState([]);
  const [reminderActivityTypeId, setReminderActivityTypeId] = useState(null);
  const [reminderTypes, setReminderTypes] = useState([]);
  const [defaultActivityTypeId, setDefaultActivityTypeId] = useState("");
  const getLocalDateTimeString = (date = new Date()) => {
    const offsetMs = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() - offsetMs);
    return localDate.toISOString().slice(0, 16);
  };
  const initialActivity = {
    type: defaultActivityTypeId,
    title: "",
    description: "",
    date: getLocalDateTimeString(),
  };
  const [newActivity, setNewActivity] = useState(initialActivity);
  const { domain, token, userDetails } = useSelector(
    (state) => state.tokenReducer
  );
  const loadActivities = React.useCallback(
    async (pageNumber = 1) => {
      setLoading(true);
      try {
        const res = await customGetAPI(
          `${domain}crm/activities/?contact_id=${contactId}&page=${pageNumber}`
        );
        setActivities(res.responseDetails.results);
        setTotalPages(Math.ceil(res.responseDetails.count / 10));
      } catch (err) {
        console.error("Failed to load activities", err);
      } finally {
        setLoading(false);
      }
    },
    [domain, contactId]
  );

  useEffect(() => {
    if (contactId) loadActivities();
  }, [contactId, loadActivities]);

  useEffect(() => {
    const localDatetime = getLocalDateTimeString(); // ISO string without timezone
    setNewActivity((prev) => ({ ...prev, date: localDatetime }));
  }, []);

  useEffect(() => {
    if (triggerAddActivity) {
      setShowAddForm(true);
      if (onAddActivityOpened) onAddActivityOpened();
    }
  }, [onAddActivityOpened, triggerAddActivity]);

  useEffect(() => {
    const fetchActivityTypeOptions = async () => {
      try {
        const res = await customGetAPI(`${domain}crm/activity-type/`);
        const types = res.responseDetails || [];
        setActivityTypes(types);
        if (types.length > 0) {
          const noteType = types.find((t) => t.label?.toLowerCase() === "note");
          const defaultTypeId = noteType ? noteType.id : types[0].id;
          setDefaultActivityTypeId(defaultTypeId);
          setNewActivity((prev) => ({
            ...prev,
            type: defaultTypeId,
          }));
          const reminderType = types.find(
            (t) => t.label?.toLowerCase() === "reminder"
          );
          setReminderActivityTypeId(reminderType?.id || null);
        }
      } catch (err) {
        console.error("Failed to fetch contact status:", err);
      }
    };

    fetchActivityTypeOptions();
  }, [domain]);

  useEffect(() => {
    loadActivities(page);
  }, [page, loadActivities]);

  useEffect(() => {
    const fetchReminderTypes = async () => {
      const res = await customGetAPI(`${domain}crm/reminder-type/`);
      setReminderTypes(res.responseDetails || []);
    };
    fetchReminderTypes();
  }, [domain]);

  const handleAddActivity = async (e) => {
    e.preventDefault();
    try {
      const now = getLocalDateTimeString();
      if (editingActivity) {
        // PATCH
        await axios.patch(
          `${domain}crm/activities/${editingActivity.id}/`,
          {
            ...newActivity,
          },
          //    {
          //   ...newActivity,
          //   date:
          //     newActivity.type === reminderActivityTypeId
          //       ? now // activities always store now
          //       : now,
          // },
          {
            headers: {
              Authorization: `token ${token}`,
            },
          }
        );
      } else {
        // POST
        await axios.post(
          `${domain}crm/activities/`,
          {
            ...newActivity,
            date: newActivity.type === reminderActivityTypeId ? now : now,
            contact: contactId,
          },
          {
            headers: {
              Authorization: `token ${token}`,
            },
          }
        );

        if (newActivity.type === reminderActivityTypeId) {
          const followUpType = reminderTypes.find(
            (t) => t.label?.toLowerCase() === "follow up"
          );
          const reminderTypeId = followUpType
            ? followUpType.id
            : reminderTypes[0]?.id || "";
          const reminderPayload = {
            title: newActivity.title,
            notes: newActivity.description || "",
            date: newActivity.date,
            type: reminderTypeId,
            priority: "medium",
            contact: contactId,
            assigned_to: userDetails?.id,
          };

          await axios.post(`${domain}crm/reminders/`, reminderPayload, {
            headers: {
              Authorization: `token ${token}`,
            },
          });
        }
      }

      setShowAddForm(false);
      setEditingActivity(null);
      setNewActivity(initialActivity);
      loadActivities();
    } catch (err) {
      console.error("Failed to save activity", err);
    }
  };

  return (
    <Box sx={{ px: 2, py: 1 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Timeline</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowAddForm(!showAddForm)}
          sx={{
            backgroundColor: "#84DEB8",
            color: "#000",
            "&:hover": { backgroundColor: "#6cc9a1" },
          }}
        >
          Add Activity
        </Button>
      </Box>

      {showAddForm && (
        <Paper sx={{ p: 2, mb: 3 }} elevation={2}>
          <form onSubmit={handleAddActivity}>
            <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
              <FormControl fullWidth>
                <InputLabel>Activity Type</InputLabel>
                <Select
                  value={newActivity.type}
                  label="Activity Type"
                  onChange={(e) =>
                    setNewActivity({ ...newActivity, type: e.target.value })
                  }
                >
                  {activityTypes.map((type) => (
                    <MenuItem key={type.value} value={type.id}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                type="datetime-local"
                label="Date"
                fullWidth
                value={newActivity.date}
                onChange={(e) =>
                  setNewActivity({ ...newActivity, date: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                disabled={
                  editingActivity !== null ||
                  newActivity.type !== reminderActivityTypeId
                }
              />
            </Box>
            <TextField
              fullWidth
              label="Title"
              value={newActivity.title}
              onChange={(e) =>
                setNewActivity({ ...newActivity, title: e.target.value })
              }
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={newActivity.description}
              onChange={(e) =>
                setNewActivity({ ...newActivity, description: e.target.value })
              }
              sx={{ mb: 2 }}
            />
            <Box display="flex" gap={2}>
              <Button variant="contained" type="submit">
                Save
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setShowAddForm(false);
                  setNewActivity(initialActivity);
                  setEditingActivity(null);
                }}
              >
                Cancel
              </Button>
            </Box>
          </form>
        </Paper>
      )}
      {loading ? (
        <Box textAlign="center" py={5}>
          <CircularProgress />
          <Typography mt={2}>Loading timeline...</Typography>
        </Box>
      ) : activities?.length > 0 ? (
        <Timeline position="right">
          {activities.map((activity, index) => (
            <TimelineItem key={activity.id}>
              <TimelineOppositeContent sx={{ flex: 0.25, pt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <AccessTime fontSize="small" sx={{ mr: 0.5 }} />
                  {format(new Date(activity.date), "MMM d, yyyy h:mm a")}
                </Typography>
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineDot
                  sx={{
                    backgroundColor:
                      activity.activity_name === "Call" && !activity.completed
                        ? "#f44336"
                        : activity.status_color,
                    color: "#fff",
                  }}
                >
                  {activity.activity_name === "Call" && !activity.completed
                    ? activityIcons.Missed_call
                    : activityIcons[activity.activity_name] || <Description />}
                </TimelineDot>
                {index < activities.length - 1 && <TimelineConnector />}
              </TimelineSeparator>
              <TimelineContent>
                <Box
                  position="relative"
                  onMouseEnter={() => setHoveredId(activity.id)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      transition: "0.2s",
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    {/* Title */}
                    <Typography variant="subtitle1" fontWeight={600}>
                      {activity.title}
                    </Typography>

                    {/* Icons shown only on hover */}
                    {hoveredId === activity.id && (
                      <Box
                        position="absolute"
                        top={8}
                        right={8}
                        display="flex"
                        gap={1}
                        zIndex={1}
                      >
                        <IconButton
                          size="small"
                          title="Edit Activity"
                          onClick={() => {
                            const dateTimeString = `${
                              activity.formatted_date
                            }T${activity.formatted_time.slice(0, 5)}`;
                            setNewActivity({
                              type: activity.type,
                              title: activity.title,
                              description: activity.description || "",
                              // date: new Date(activity.date)
                              //   .toISOString()
                              //   .slice(0, 16),
                              date: dateTimeString,
                            });
                            setEditingActivity(activity);
                            setShowAddForm(true);
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>

                        <IconButton
                          size="small"
                          title="Delete Activity"
                          onClick={() => {
                            setShowAddForm(false);
                            setEditingActivity(null);
                            showConfirmDialog({
                              title: "Delete Activity",
                              description: `Are you sure you want to delete "${activity.title}"?`,
                              onConfirm: async () => {
                                try {
                                  await axios.delete(
                                    `${domain}crm/activities/${activity.id}/`,
                                    {
                                      headers: {
                                        Authorization: `token ${token}`,
                                      },
                                    }
                                  );
                                  loadActivities();
                                } catch (err) {
                                  console.error(
                                    "Failed to delete activity:",
                                    err?.response?.data || err.message
                                  );
                                }
                              },
                            });
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    )}

                    {/* Description */}
                    {activity.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        mt={0.5}
                      >
                        {activity.description}
                      </Typography>
                    )}

                    {/* Chips */}
                    <Box mt={1} display="flex" gap={1} flexWrap="wrap">
                      <Chip
                        label={activity.activity_name.replace(/_/g, " ")}
                        size="small"
                      />
                      {activity.activity_name === "Call" &&
                      !activity.completed ? (
                        <Chip label="Missed" size="small" color="error" />
                      ) : activity.completed ? (
                        <Chip label="Completed" size="small" color="success" />
                      ) : null}
                    </Box>
                  </Paper>
                </Box>
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      ) : (
        <Box textAlign="center" mt={4}>
          <AccessTime fontSize="large" color="disabled" />
          <Typography color="text.secondary" mt={1}>
            No activities recorded
          </Typography>
          <Button
            variant="outlined"
            sx={{ mt: 2 }}
            onClick={() => setShowAddForm(true)}
          >
            Add First Activity
          </Button>
        </Box>
      )}
      <ConfirmDialog />
      {activities.length > 0 && totalPages > 1 && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          rowGap={1}
          mt={2}
        >
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            Showing page {page} of {totalPages}
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, val) => setPage(val)}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
}
